import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, memo, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useAppSelector, useAppDispatch } from '@loci/hooks';
import { selectVisibleNotifications, selectNotifications, selectNotificationSettings, removeNotification, } from '@loci/store';
import { Toast } from './Toast';
import { TOAST_TRANSITION } from './Toast.styles';
import { TOAST_SLOT_VARIANTS, readStackGap } from './toastStackSlot';
import { useToastStackTop } from './toastStackTop';
/**
 * ToastContainer component that displays notifications from the Redux store.
 *
 * @remarks
 * This component renders visible notifications using the Toast component
 * and handles dismissal actions by dispatching to Redux.
 *
 * The component uses typed Redux hooks from @loci/hooks for type safety.
 *
 * @remarks Positioning contract (WEB-695)
 * Positioning is NOT expressed in Tailwind utilities here. `apps/web`'s
 * Tailwind `content` globs do not scan `packages/ui/**` and `@loci/ui` ships
 * no CSS of its own, so any utility used only inside this package is dropped
 * from the compiled bundle — which is how this container shipped with a
 * computed `z-index: auto` (losing to the sticky chrome at `--z-chrome: 60`)
 * and a `md:` reposition that never rendered. Instead the host app owns a
 * single semantic `.loci-toast-region` rule in `apps/web/styles/surfaces/toast.css`
 * that carries the z rung (`--z-toast`) and the chrome-derived top inset
 * (`--toast-inset-top`). Any other host embedding `<ToastContainer />` must
 * provide that class, and must not reintroduce a local z number here.
 *
 * WEB-1099: this region owns the stack's top. When a toast opens an empty
 * stack it measures once against any pinned surface chrome (`data-ln-chrome`)
 * and holds that top until the stack empties — see `toastStackTop.ts`.
 *
 * WEB-1123: each toast sits in a slot that opens and closes in real layout, so
 * back-to-back toasts push each other apart instead of being painted over one
 * another mid-animation — see `toastStackSlot.ts`.
 *
 * `data-toast-region` is the stable hook page-scoped toast surfaces use to
 * portal themselves into this same column, so
 * independent toast surfaces stack instead of overlapping.
 *
 * @example
 * ```tsx
 * import { ToastContainer } from '@loci/ui';
 *
 * function App() {
 *   return (
 *     <>
 *       <YourContent />
 *       <ToastContainer />
 *     </>
 *   );
 * }
 * ```
 */
export const ToastContainer = memo(function ToastContainer() {
    const dispatch = useAppDispatch();
    const visibleNotifications = useAppSelector(selectVisibleNotifications);
    const allNotifications = useAppSelector(selectNotifications);
    const settings = useAppSelector(selectNotificationSettings);
    const regionRef = useRef(null);
    useToastStackTop(regionRef);
    const readGap = useCallback(() => readStackGap(regionRef.current), []);
    const handleDismiss = useCallback((id) => {
        dispatch(removeNotification(id));
    }, [dispatch]);
    // Calculate hidden count for overflow indicator
    const hiddenCount = Math.max(0, allNotifications.length - settings.maxVisible);
    const hasHiddenNotifications = hiddenCount > 0;
    return (_jsxs("section", { ref: regionRef, className: "loci-toast-region", "data-toast-region": true, "aria-label": "Notifications", children: [_jsx(AnimatePresence, { children: visibleNotifications.map((notification) => (_jsx(motion.div, { custom: readGap, variants: TOAST_SLOT_VARIANTS, initial: "closed", animate: "open", exit: "closed", transition: TOAST_TRANSITION, children: _jsx(Toast, { notification: notification, onDismiss: handleDismiss }) }, notification.id))) }), hasHiddenNotifications && (_jsxs("div", { className: "text-sm text-gray-600 dark:text-gray-400 px-4 py-2 bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700", role: "status", "aria-live": "polite", children: ["+", hiddenCount, " more notification", hiddenCount > 1 ? 's' : ''] }))] }));
});
